/**
 * Перенос `lib/src/utils/interceptors/path_interceptor.dart`.
 *
 * Подставляет в адрес запроса сохранённый адрес сервера и текущий язык:
 * пути в `Endpoints` записаны с литералами `${baseUrl}` и `${lang}`, и
 * настоящие значения известны только в момент запроса.
 */

import { Endpoints } from '@/config/endpoints';
import { defaultLanguage } from '@/domain/models/appLanguage';
import type { HttpError } from '@/utils/exceptions/appException';
import type { HttpInterceptor, HttpRequest, HttpResponse } from '@/utils/http/types';
import { requestWithPath } from '@/utils/http/types';
import { StorageHelper } from '@/utils/storageHelper';

const HTTP_PREFIX = 'http://';

const HTTPS_PREFIX = 'https://';

/** `RegExp(r'/+')` из Dart — подряд идущие слеши. */
const REPEATED_SLASHES = /\/+/g;

/** Схема в начале адреса: её слеши схлопывать нельзя. */
function protocolOf(endpoint: string): string {
  if (endpoint.startsWith(HTTP_PREFIX)) return HTTP_PREFIX;
  if (endpoint.startsWith(HTTPS_PREFIX)) return HTTPS_PREFIX;
  return '';
}

/**
 * Схлопывает повторяющиеся слеши во всём адресе, кроме `http://`/`https://`.
 *
 * Нужна потому, что и сохранённый адрес сервера, и куски путей из
 * `Endpoints` могут прийти со своим слешем на стыке: `…uz//uz/api/v1/…`.
 */
export function cleanEndpoint(endpoint: string): string {
  const protocol = protocolOf(endpoint);
  const rest = endpoint.slice(protocol.length);
  const cleaned = rest.replace(REPEATED_SLASHES, '/');
  return protocol + cleaned;
}

/** Аналог `dev.log(…, name: 'PathInterceptor')`. */
function log(message: string): void {
  console.debug(`[PathInterceptor] ${message}`);
}

/**
 * `String.replaceAll(pattern, replacement)` из Dart.
 *
 * В JS замена-строка понимает `$&`, `` $` `` и `$1`, а адрес сервера или
 * код языка — это произвольный текст. Функция-замена отключает разбор
 * подстановок, и поведение совпадает с дартовым.
 */
function replaceAllLiteral(source: string, pattern: string, replacement: string): string {
  return source.replaceAll(pattern, () => replacement);
}

export class PathInterceptor implements HttpInterceptor {
  onRequest = (request: HttpRequest): HttpRequest => {
    const initialPath = request.path;
    const baseUrl = StorageHelper.instance.baseUrl;
    const language = StorageHelper.instance.language ?? defaultLanguage;

    // Порядок важен: сначала адрес сервера, потом язык, и после каждой
    // подстановки — чистка слешей. Так это и было в Dart.
    const withBaseUrl =
      baseUrl !== null
        ? cleanEndpoint(replaceAllLiteral(initialPath, Endpoints.baseUrlPlaceholder, baseUrl))
        : initialPath;

    // `language.param` из Dart: значением `AppLanguage` в порте служит сам
    // `param`, поэтому подставляется он же.
    const path = cleanEndpoint(
      replaceAllLiteral(withBaseUrl, Endpoints.langPlaceholder, language),
    );

    log(`request path: ${initialPath}, result path: ${path}`);

    return requestWithPath(request, path);
  };

  onResponse = (response: HttpResponse): HttpResponse => {
    log(`response on request: ${response.request.path}`);
    return response;
  };

  onError = (error: HttpError, request: HttpRequest): HttpError => {
    log(`error on request: ${request.path}, error: ${String(error.cause ?? null)}`);
    return error;
  };
}
